import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { FaPencilAlt, FaCaretDown } from 'react-icons/fa';
import Header from '../../../../../../header/header';
import Navvbar from '../../../../../../header/Navvbar';
import Footer from "../../../../../../footer.js";
import Swal from 'sweetalert2'; // Beautiful alerts
import Sidebar from '../../../../../../Sidebar.js';
import './ShippingEdit.css';

const ShippingEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    company: 'Royal mail',
    service: '',
    usuallyDeparts: '',
    outboundDays: null,
    inboundDays: null,
    price: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch existing shipping type data on mount
  useEffect(() => {
    const fetchShipping = async () => {
      try {
        const res = await fetch(`https://hms.backend.hiragroupcloud.com/api/shipping/${id}`);
        const data = await res.json();

        if (data.success) {
          setFormData({
            ...data.data,
            outboundDays: data.data.outboundDays ?? null,
            inboundDays: data.data.inboundDays ?? null,
            price: data.data.price ?? null,
          });
        } else {
          Swal.fire({
            title: 'Not Found',
            text: 'Shipping type not found',
            icon: 'error',
            confirmButtonColor: '#004aad'
          });
          navigate('/caseSetting');
        }
      } catch (err) {
        Swal.fire({
          title: 'Error',
          text: 'Error loading shipping type',
          icon: 'error',
          confirmButtonColor: '#004aad'
        });
        navigate('/caseSetting');
      } finally {
        setLoading(false);
      }
    };

    fetchShipping();
  }, [id, navigate]);

  // Handle input changes (convert number fields properly)
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (['outboundDays', 'inboundDays', 'price'].includes(name)) {
      updatedValue = value === '' ? null : Number(value);
    }

    setFormData(prev => ({ ...prev, [name]: updatedValue }));
    setError('');
  };

  // Update shipping type
  const handleSave = async () => {
    // Required fields validation
    if (
      !formData.service ||
      formData.outboundDays == null ||
      formData.inboundDays == null
    ) {
      Swal.fire({
        title: 'Missing Fields',
        text: 'Please fill all required fields (Service, Outbound & Inbound Days)',
        icon: 'warning',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    setSaving(true);
    setError('');

    try {
      const body = {
        ...formData,
        service: formData.service.trim(),
        usuallyDeparts: formData.usuallyDeparts?.trim() || '',
      };

      const res = await fetch(`https://hms.backend.hiragroupcloud.com/api/shipping/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (data.success) {
        // Success: Show alert for 2 seconds then navigate
        Swal.fire({
          title: 'Success!',
          text: 'Shipping Type Updated Successfully!',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false
        });

        setTimeout(() => {
          navigate('/caseSetting');
        }, 2000);
      } else {
        // Backend error
        Swal.fire({
          title: 'Error',
          text: data.message || 'Error updating shipping type',
          icon: 'error',
          confirmButtonColor: '#004aad'
        });
      }
    } catch (err) {
      // Network or unexpected error
      Swal.fire({
        title: 'Network Error',
        text: 'Failed to update shipping type. Please try again.',
        icon: 'error',
        confirmButtonColor: '#004aad'
      });
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="se-loading">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="se-header-wrap">
        <Header />
        <Navvbar />
      </div>

      <div className="se-container">
        <div className="row se-row">
          {/* Sidebar */}
          <div className="col-md-2 se-sidebar">
            <Sidebar />
          </div>

          {/* Main Form */}
          <div className="col-md-10 se-main">
            <div className="se-main-inner">
              <h1 className="se-page-title">Edit Shipping Type</h1>
              <h2 className="se-section-title">Shipping Type Details</h2>

              <div className="se-form-box">
                {/* Company */}
                <div className="se-row-field">
                  <label className="se-label">
                    Company <span className="se-required">*</span>
                  </label>
                  <div className="se-select-wrap">
                    <select
                      name="company"
                      value={formData.company || ''}
                      onChange={handleChange}
                      className="se-select"
                    >
                      <option>Royal mail</option>
                      <option>Uber</option>
                      <option>Local Driver</option>
                      <option>Daewoo</option>
                      <option>Tcs</option>
                      <option>Bykea</option>
                    </select>
                    <FaCaretDown className="se-caret" />
                  </div>
                </div>

                {/* Service */}
                <div className="se-row-field">
                  <label className="se-label">
                    Service <span className="se-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="service"
                    value={formData.service || ''}
                    onChange={handleChange}
                    className="se-input"
                  />
                </div>

                {/* Usually Departs */}
                <div className="se-row-field">
                  <label className="se-label">Usually Departs</label>
                  <input
                    type="text"
                    name="usuallyDeparts"
                    value={formData.usuallyDeparts || ''}
                    onChange={handleChange}
                    className="se-input"
                  />
                </div>

                {/* Outbound Transit */}
                <div className="se-row-field se-row-small-gap">
                  <label className="se-label">
                    Outbound Transit <span className="se-required">*</span>
                  </label>
                  <input
                    type="number"
                    name="outboundDays"
                    value={formData.outboundDays ?? ''}
                    onChange={handleChange}
                    className="se-input-num"
                    min="0"
                    step="1"
                  />
                  <span className="se-hint">Days</span>
                </div>

                {/* Inbound Transit Days */}
                <div className="se-row-field se-row-small-gap se-row-20">
                  <label className="se-label">
                    Inbound Transit Days <span className="se-required">*</span>
                  </label>
                  <input
                    type="number"
                    name="inboundDays"
                    value={formData.inboundDays ?? ''}
                    onChange={handleChange}
                    className="se-input-num"
                    min="0"
                    step="1"
                  />
                </div>

                {/* Price – Optional */}
                <div className="se-row-field se-row-small-gap se-row-20">
                  <label className="se-label">Price</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price ?? ''}
                    onChange={handleChange}
                    step="0.01"
                    className="se-input-num"
                    min="0"
                  />
                </div>

                {/* Update Button */}
                <div className="se-save-wrap">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`se-save-btn ${saving ? 'se-btn-loading' : ''}`}
                  >
                    <FaPencilAlt /> {saving ? 'Saving...' : 'Update'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="se-footer-wrap">
        <Footer />
      </div>
    </>
  );
};

export default ShippingEdit;